import { Component, OnInit, Input, OnDestroy, ElementRef, Renderer2, Inject } from '@angular/core';
import { Subject } from 'rxjs';
import { CommonModule, DOCUMENT } from '@angular/common';
import get from 'lodash-es/get';
import has from 'lodash-es/has';

import { Storycard } from '@finder/shared/components/storycard/storycard.interface';
import { Header } from '@finder/shared/services/header-alignment/header-alignment.interface';
import { HeaderAlignmentService } from '@finder/shared/services/header-alignment/header-alignment.service';
import { IDynamicPolylineAttributes } from '@finder/shared/components/polyline/polyline.constants';
import { ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { CtaButton } from '@finder/shared/components/campaign-cta/campaign-cta-interface';
import { ButtonComponent } from '../button/button.component';
import { BorderImageDirective } from '@finder/shared/directives/border-image/border-image.directive';
import { ComponentHeaderComponent } from '../component-header/component-header.component';
import { DynamicPolylineComponent } from '../dynamic-polyline/dynamic-polyline.component';
import { GenericTextComponent } from '../generic-text/generic-text.component';
import { InfoModalComponent } from '../info-modal/info-modal.component';
import { LottieComponent } from '../lottie/lottie.component';
import { MediaGalleryComponent } from '../media-gallery/media-gallery.component';
import { PictureComponent } from '../picture/picture.component';
import { StorycardComponent } from '../storycard/storycard.component';

@Component({
    selector: 'finder-descriptive-text',
    templateUrl: './descriptive-text.component.html',
    styleUrls: ['./descriptive-text.component.scss'],
    standalone: true,
    imports: [
        BorderImageDirective, // finderBorderImage
        ButtonComponent,
        CommonModule,
        ComponentHeaderComponent,
        DynamicPolylineComponent,
        GenericTextComponent,
        InfoModalComponent,
        LottieComponent,
        MediaGalleryComponent,
        PictureComponent,
        StorycardComponent,
    ]
})
export class DescriptiveTextComponent implements OnInit, OnDestroy {

    @Input() data: any;
    @Input() useMediaGallery = false;
    @Input() imgPreload = false;

    STORYCARD = 'storycard';
    layout;
    storyCardData: Storycard;
    headerData: Header;
    mediaEngineConfig;
    polyline: IDynamicPolylineAttributes;
    button: CtaButton;
    position: string;

    private destroy$ = new Subject<void>();

    constructor(
        private elementRef: ElementRef,
        private headerAlignmentService: HeaderAlignmentService,
        private viewportService: ViewportDetectionService,
        private renderer: Renderer2,
        @Inject(DOCUMENT) private document: Document,
    ) {}

    /* istanbul ignore next */
    ngOnDestroy() {
        // Ignore the if just for coverage, there's no need to test unsubscribe
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit() {
        this.getPosition();
        this.setLayout();
        this.setupMediaGallery();
        this.setupPolyLine();
        this.setButton();
    }

    /* istanbul ignore next */
    getElementReference() {
        // Added to make testing reachable
        return this.elementRef.nativeElement;
    }

    setupMediaGallery() {
        // Make sure the slides data exists before getting started
        if (!this.useMediaGallery) {
            // Preload images
            return this.preloadCheck();
        }

        // Enable the medial gallery
        this.useMediaGallery = true;

        // Pass media engine config directly from API
        this.mediaEngineConfig  = this.data.mediaEngine;
    }

    setupPolyLine() {
        if (!has(this.data, 'genericText.polyline')) {
            return;
        }

        // Pull the values from the config
        const [starting, color, transition, width] = get(this.data, 'genericText.polyline', '').split(':');
        // Set the values on the object
        this.polyline = {
            starting,
            color,
            transition,
            width
        };
    }

    private setLayout() {
        if (get(this.data, 'position', '') !== 'inline') {
            return;
        }

        this.layout = this.STORYCARD;

        // Format the header data
        this.headerData = this.headerAlignmentService.getHeaderAlignment(get(this.data, 'genericText'));

        // Format the story card data
        this.storyCardData = {
            sections: [{
                copy: [get(this.data, 'genericText.text')],
                image: {
                    alt: get(this.data, 'media[0].alt', ''),
                    desktop: get(this.data, 'media[0].desktop', ''),
                    media: get(this.data, 'media[0].media', ''),
                    // Allow fallback to desktop image
                    mobile: get(this.data, 'media[0].mobile', get(this.data, 'media[0].desktop', '')),
                }
            }]
        };
    }

    private preloadCheck() {
        if (!this.imgPreload) {
            return;
        }

        // Check viewport
        const isDesktop = this.viewportService.isDesktop();
        let imgUrls = this.data?.media?.reduce((result, img) => {
            // Add desktop or mobile (with desktop fallback)
            result.push(isDesktop ?
                img.desktop :
                img.mobile || img.desktop
            );

            // Return result
            return result;
        }, []) || /* istanbul ignore next */[];

        // Remove empties
        imgUrls = imgUrls.filter(img => img !== void 0);

        this.preloadImages(imgUrls);
    }

    private preloadImages(images: string[]) {
        images.forEach(img => {
            // Create link element with preload props
            const preload: Element = this.document.createElement('link');
            preload.setAttribute('rel', 'preload');
            preload.setAttribute('as', 'image');
            preload.setAttribute('href', img);

            // Add the link element
            this.renderer.appendChild(this.document.head, preload);
        });
    }

    private setButton() {
        if (!this.data?.button) {
            return;
        }

        this.button = {
            link: this.data.button.link,
            target: '_self',
            label: this.data.button.label,
            theme: this.data.button.theme
        };
    }

    private getPosition() {
        this.position = this.viewportService.isDesktop() ? this.data?.position : this.data?.positionMobile;
    }
}
